import React, { useRef, useState } from 'react';

import { useCreateCard, useCreateFace, useEstimateSdk } from '@vlabs/api/artemy/client';
import type {
  CardId, SampleId, SdkDescriptor,
} from '@vlabs/api/artemy/model';
import { PlusIcon } from '@vlabs/icons';
import { Control, useModal } from '@vlabs/uikit';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { toast } from 'react-toastify';

import { AttachFaceToCardPoint3 } from '@vlabs/monokit/features/attach-face-to-card';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './AttachFaceToCardButton.module.sass';
import { useAttachFaceToCardButtonTranslation } from './i18n';
import { CreateCardFormPoint3 } from '../../entities/cards/create-card';

type State = 'attach' | 'create';

interface AttachFaceToCardButtonPoint3Props extends Omit<ButtonProps, 'onClick'>, WithDataTestid {
  sampleId: SampleId;
  onAttachFaceToCard?: ({ fromState, cardId }: {
    fromState: State;
    cardId: CardId;
  }) => void;
}

const displayName = 'AttachFaceToCardButtonPoint3';
const testid = buildTestid(displayName);

function AttachFaceToCardButtonPoint3({
  sampleId,
  onAttachFaceToCard = undefined,
  'data-testid': dataTestId = undefined,
  children,
  ...props
}: AttachFaceToCardButtonPoint3Props): React.JSX.Element {
  const { t } = useAttachFaceToCardButtonTranslation();
  const [modalContent, setModalContent] = useState<State>('attach');

  const modal = useModal(modalContent === 'create' ? { size: 'large' } : { size: 'small' });

  const estimateSdk = useEstimateSdk();
  const createFace = useCreateFace();
  const createCard = useCreateCard();

  const isFacePending = estimateSdk.isPending || createFace.isPending;
  const isPending = createCard.isPending || isFacePending;
  const isEstimatePendingRef = useRef(false);
  const isCreateFacePendingRef = useRef(false);
  const isCreateCardPendingRef = useRef(false);

  const showFaceCreationError = () => {
    toast.error(t('toast_error'));
  };

  const estimateFaceDescriptor = (onSuccess: (descriptor: SdkDescriptor) => void) => {
    if (isEstimatePendingRef.current) return;
    isEstimatePendingRef.current = true;

    estimateSdk.mutate({
      data: { samples: [sampleId] },
      params: {
        estimate_face_descriptor: 1,
        image_type: 1,
      },
    }, {
      onSuccess: (response) => {
        const imageEstimation = response.images_estimations[0];
        if (!imageEstimation || 'error' in imageEstimation) {
          showFaceCreationError();
          return;
        }

        const descriptor = imageEstimation.estimations[0]
          ?.face?.detection?.attributes?.descriptor?.sdk_descriptor;
        if (!descriptor) {
          showFaceCreationError();
          return;
        }

        onSuccess(descriptor);
      },
      onError: showFaceCreationError,
      onSettled: () => {
        isEstimatePendingRef.current = false;
      },
    });
  };

  const attachSampleToCard = (cardId: CardId) => {
    if (isEstimatePendingRef.current || isCreateFacePendingRef.current) return;

    estimateFaceDescriptor((descriptor) => {
      if (isCreateFacePendingRef.current) return;
      isCreateFacePendingRef.current = true;

      createFace.mutate({
        data: {
          card_id: cardId,
          avatar: `/1/samples/faces/${sampleId}`,
          attribute: {
            face_descriptors: [descriptor],
            face_descriptor_samples: [sampleId],
          },
        },
      }, {
        onSuccess: () => {
          setModalContent('attach');
          modal.close();
          toast.success(t('toast_success'));
          onAttachFaceToCard?.({ fromState: modalContent, cardId });
        },
        onError: showFaceCreationError,
        onSettled: () => {
          isCreateFacePendingRef.current = false;
        },
      });
    });
  };

  return (
    <React.Fragment>
      {modal.wrap(
        <React.Fragment>
          {modalContent === 'attach' && (
            <React.Fragment>
              <AttachFaceToCardPoint3
                isPending={isPending}
                onSubmit={attachSampleToCard}
              />
              <div className={st.Actions}>
                <Control.Button
                  className={st.ButtonWrapper}
                  disabled={isPending}
                  onClick={() => setModalContent('create')}
                  variant="outlined"
                >
                  {t('button_create')}
                </Control.Button>

                <Control.Button
                  className={st.ButtonWrapper}
                  disabled={isPending}
                  kind="secondary"
                  onClick={modal.close}
                  variant="outlined"
                >
                  {t('button_cancel')}
                </Control.Button>
              </div>
            </React.Fragment>
          )}

          {modalContent === 'create' && (
            <CreateCardFormPoint3
              onCancel={() => {
                if (isPending) return;
                setModalContent('attach');
              }}
              onSubmit={({ fio, lists }) => {
                if (
                  isCreateCardPendingRef.current
                  || isEstimatePendingRef.current
                  || isCreateFacePendingRef.current
                ) return;
                isCreateCardPendingRef.current = true;

                createCard.mutate({
                  data: {
                    user_data: { fio },
                    lists: lists?.length ? lists : undefined,
                  },
                }, {
                  onSuccess: ({ card_id: cardId }) => {
                    attachSampleToCard(cardId);
                  },
                  onError: () => {
                    toast.error(t('toast_create_card_error'));
                  },
                  onSettled: () => {
                    isCreateCardPendingRef.current = false;
                  },
                });
              }}
            />
          )}
        </React.Fragment>,
      )}

      <Control.Button
        icon={<PlusIcon />}
        kind="secondary"
        onClick={modal.open}
        variant="filled"
        {...testid(dataTestId)}
        {...props}
        disabled={isPending || props.disabled}
      >
        {children ?? t('button_face_to_card')}
      </Control.Button>
    </React.Fragment>
  );
}

AttachFaceToCardButtonPoint3.displayName = displayName;

export { AttachFaceToCardButtonPoint3 };
